import { type MutableRefObject, useCallback } from 'react';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import { useRemixThinkingBarProgress } from './useRemixThinkingBarProgress.ts';

type ExperienceTracker = ReturnType<typeof useAIMateExperienceTracker>;

type UseRemixPreviewFlowOptions = {
	entryPoint: 'editor' | 'renderer';
	experienceTracker: ExperienceTracker;
	dynamicUiType: string | undefined;
	selectedSubTypeRef: MutableRefObject<string | undefined>;
	// Refs (rather than direct values) because `openAIFCStagingModal` is produced by
	// `useRemixToAIFC`, which itself takes the `onActionReady` returned by this hook —
	// the ref breaks that cycle. The other refs are mutated by container event handlers
	// after this hook runs, so we read `.current` lazily at action time.
	openAIFCStagingModalRef: MutableRefObject<() => void>;
	isRemixCancelledRef: MutableRefObject<boolean>;
	onAfterOpenPreview?: () => void;
};

type UseRemixPreviewFlowResult = {
	showProgressBar: boolean;
	isComplete: boolean;
	onCompleteAnimationFinished: () => void;
	onActionReady: () => void;
	resetCompletion: () => void;
};

/**
 * Wiring for the Editor/Renderer remix preview flow: experience tracking,
 * opening the AIFC staging modal, the progress-bar deferral, and the
 * `isRemixCancelled` guard around `onActionReady`.
 */
export const useRemixPreviewFlow = ({
	entryPoint,
	experienceTracker,
	dynamicUiType,
	selectedSubTypeRef,
	openAIFCStagingModalRef,
	isRemixCancelledRef,
	onAfterOpenPreview,
}: UseRemixPreviewFlowOptions): UseRemixPreviewFlowResult => {
	const openPreview = useCallback(() => {
		experienceTracker.succeed({ name: ExperienceName.REMIX_START });
		// Start before the modal content mounts.
		experienceTracker.start({
			name: ExperienceName.REMIX_RENDERS_IN_MODAL,
			id: ExperienceName.REMIX_RENDERS_IN_MODAL,
			attributes: {
				product: 'confluence',
				remixType: dynamicUiType,
				remixSubtype: selectedSubTypeRef.current,
				entryPoint,
			},
		});
		openAIFCStagingModalRef.current();
		onAfterOpenPreview?.();
	}, [
		entryPoint,
		experienceTracker,
		dynamicUiType,
		selectedSubTypeRef,
		openAIFCStagingModalRef,
		onAfterOpenPreview,
	]);

	const {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		signalActionReady,
		resetCompletion,
	} = useRemixThinkingBarProgress({ onOpenPreview: openPreview });

	const onActionReady = useCallback(() => {
		if (!isRemixCancelledRef.current) {
			signalActionReady();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- ref identity is stable; reading .current does not need to be a dep
	}, [signalActionReady]);

	return {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		onActionReady,
		resetCompletion,
	};
};
